// ─── Listing prices in their own currency, with an approximate conversion ─────
// A listing's price is stored in the currency the school chose (retreats.currency,
// classes.currency). It is always shown in that currency — "฿12,000" — because
// that is what the student pays the school. Next to it, an approximate figure in
// the visitor's currency: "฿12,000 · ≈ $360".
//
// Rates come from Frankfurter (ECB reference rates), fetched at most once a day
// and cached in localStorage. Until they arrive, or if the fetch fails, the
// static table in PaymentService is used, so a price is never missing.
//
// This module is display-only. PaymentService's CURRENCIES rates still decide
// what Razorpay charges, and nothing here changes that.
import { useEffect, useSyncExternalStore } from "react";
import { CURRENCIES, RATES_AS_OF } from "./PaymentService";

export { CURRENCIES };

const FX_URL = "https://api.frankfurter.dev/v1/latest?base=USD";
const FX_KEY = "ys_fx_v1";
const FX_MAX_AGE = 24 * 60 * 60 * 1000;
const CUR_KEY = "yogastay_currency";          // existing key: keep visitors' saved choice

// Frankfurter (ECB) does not publish AED or NPR. Both are pegged, so they are
// derived exactly rather than left stale: AED to USD, NPR to INR.
const AED_PER_USD = 3.6725;
const NPR_PER_INR = 1.6;

const staticRates = () => Object.fromEntries(Object.values(CURRENCIES).map(c => [c.code, c.rate]));

const store = { rates: staticRates(), asOf: RATES_AS_OF, live: false };
const listeners = new Set();
const emit = () => listeners.forEach(l => l());

function withPegs(rates) {
  const r = { ...rates, USD: 1, AED: AED_PER_USD };
  if (r.INR) r.NPR = r.INR * NPR_PER_INR;
  return r;
}

function applyRates(rates, asOf) {
  // Only currencies we list; anything Frankfurter lacks keeps its static rate.
  const next = { ...store.rates };
  for (const code of Object.keys(CURRENCIES)) if (rates[code] > 0) next[code] = rates[code];
  store.rates = withPegs(next);
  store.asOf = asOf;
  store.live = true;
  emit();
}

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

let loading = null;
/** Load cached rates, refreshing from Frankfurter when older than a day. Safe to call often. */
export function loadRates() {
  if (loading) return loading;
  loading = (async () => {
    let cached = null;
    try { cached = JSON.parse(safeGet(FX_KEY) || "null"); } catch {}
    if (cached?.rates) applyRates(cached.rates, cached.asOf);
    if (cached?.fetchedAt && Date.now() - cached.fetchedAt < FX_MAX_AGE) return;
    try {
      const res = await fetch(FX_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      if (!d?.rates) throw new Error("no rates");
      applyRates(d.rates, d.date);
      safeSet(FX_KEY, JSON.stringify({ rates: d.rates, asOf: d.date, fetchedAt: Date.now() }));
    } catch (e) {
      // Stale cache or the static table stays in use; retry on the next load.
      console.warn("[money] exchange rates unavailable, using", store.live ? `cached ${store.asOf}` : `static ${RATES_AS_OF}`, e?.message);
      loading = null;
    }
  })();
  return loading;
}

const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const getRates = () => store.rates;

/** Current USD-based rates; re-renders when live rates arrive. */
export function useFxRates() {
  useEffect(() => { loadRates(); }, []);
  return useSyncExternalStore(subscribe, getRates, getRates);
}

// ── Conversion ───────────────────────────────────────────────────────────────

/** amount in `from` → amount in `to`. NaN for an unknown currency. */
export function convert(amount, from = "USD", to = "USD", rates = store.rates) {
  const a = Number(amount);
  if (from === to) return a;
  const rf = rates[from], rt = rates[to];
  return rf > 0 && rt > 0 ? (a / rf) * rt : NaN;
}

export const toUsdLive = (amount, from = "USD", rates = store.rates) => convert(amount, from, "USD", rates);

/**
 * Round an approximate figure so it does not pretend to precision it lacks:
 * two significant figures under 1,000 (356 → 360), three above (1,843 → 1,840;
 * 6,718,000 → 6,720,000). Under 10 stays whole.
 */
export function roundApprox(n) {
  const v = Math.abs(Number(n));
  if (!Number.isFinite(v) || v === 0) return 0;
  if (v < 10) return Math.round(n);
  const sig = v < 1000 ? 2 : 3;
  const p = Math.pow(10, Math.floor(Math.log10(v)) - sig + 1);
  return Math.round(n / p) * p;
}

// ── Formatting ───────────────────────────────────────────────────────────────

/** "฿12,000", "€1,650", "$95.50". Whole numbers unless the amount has cents. */
export function fmtMoney(amount, code = "USD") {
  const cur = CURRENCIES[code];
  const a = Number(amount) || 0;
  const cents = cur?.subunit !== 1 && Math.round(a * 100) % 100 !== 0 && a < 100;
  const num = a.toLocaleString(code === "INR" ? "en-IN" : "en-US", { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
  return cur ? `${cur.symbol}${num}` : `${num} ${code}`;
}

/** "฿6,000" or "฿6,000 – ฿8,000". */
export const fmtRange = (lo, hi, code = "USD") =>
  lo === hi ? fmtMoney(lo, code) : `${fmtMoney(lo, code)} – ${fmtMoney(hi, code)}`;

/** Add amounts up per currency — never across currencies. { THB: 18000, USD: 500 } */
export function sumByCurrency(rows, amountOf, currencyOf) {
  const out = {};
  for (const r of rows || []) {
    const a = Number(amountOf(r)) || 0;
    if (!a) continue;
    const c = currencyOf(r) || "USD";
    out[c] = (out[c] || 0) + a;
  }
  return out;
}

/** "฿18,000 + $500"; `fallback` currency for an empty total ("$0"). */
export function fmtTotals(totals, fallback = "USD") {
  const parts = Object.entries(totals || {}).filter(([, a]) => a).sort((x, y) => y[1] - x[1]);
  return parts.length ? parts.map(([c, a]) => fmtMoney(Math.round(a), c)).join(" + ") : fmtMoney(0, fallback);
}

/** Scale every currency's total by the same factor (e.g. a school's share). */
export const scaleTotals = (totals, factor) =>
  Object.fromEntries(Object.entries(totals || {}).map(([c, a]) => [c, a * factor]));

/**
 * bookings.total_price has no currency column: it is in the booked class's
 * currency. Tags each row with `_currency`, looked up in one request.
 * `get(path)` is the caller's own REST getter (it carries their token).
 */
export async function withClassCurrency(rows, get) {
  const list = Array.isArray(rows) ? rows : [];
  const ids = [...new Set(list.map(r => r.class_id).filter(Boolean))];
  if (!ids.length) return list.map(r => ({ ...r, _currency: "USD" }));
  const cls = await get(`classes?id=in.(${ids.join(",")})&select=id,currency`);
  const cur = Object.fromEntries((Array.isArray(cls) ? cls : []).map(c => [c.id, c.currency || "USD"]));
  return list.map(r => ({ ...r, _currency: cur[r.class_id] || "USD" }));
}

/**
 * A listing price as the visitor sees it: converted into their chosen
 * currency, whole units, nothing else ("$125", "€115"). Students see one
 * currency everywhere; the listing's own currency stays behind the scenes
 * (schools, dashboards and the enquiry sent to the school still use it).
 */
export function fmtIn(amount, from = "USD", visitor = "USD", rates = store.rates) {
  const v = convert(amount, from, visitor, rates);
  return Number.isFinite(v) ? fmtMoney(Math.round(v), visitor) : fmtMoney(amount, from);
}

/** (amount, listingCurrency) => price string in the visitor's currency. */
export function usePrice() {
  const [visitor] = useVisitorCurrency();
  const rates = useFxRates();
  return (amount, from = "USD") => fmtIn(amount, from, visitor, rates);
}

/** "≈ $360", or null when no conversion is needed or possible. */
export function approxText(amount, from, visitor, rates = store.rates) {
  if (!(Number(amount) > 0) || !from || !visitor || from === visitor) return null;
  const v = convert(amount, from, visitor, rates);
  return Number.isFinite(v) ? `≈ ${fmtMoney(roundApprox(v), visitor)}` : null;
}

/** "฿12,000 · ≈ $360" (or just "฿12,000"). */
export function priceWithApprox(amount, from, visitor, rates = store.rates) {
  const a = approxText(amount, from, visitor, rates);
  return a ? `${fmtMoney(amount, from)} · ${a}` : fmtMoney(amount, from);
}

/** The note shown under prices on detail pages and the enquiry form. */
export const convertedNote = (code) =>
  `Converted prices are approximate. You’ll pay the school directly in ${code}.`;

// ── Visitor currency ─────────────────────────────────────────────────────────

// Time zone first: it reflects where the device is, while the locale is very
// often "en-US" whatever the country. Locale region is the fallback.
const TZ_CURRENCY = [
  [/^Asia\/(Kolkata|Calcutta)$/, "INR"], [/^Asia\/Bangkok$/, "THB"], [/^Asia\/(Jakarta|Makassar|Jayapura|Pontianak)$/, "IDR"],
  [/^Asia\/(Kathmandu|Katmandu)$/, "NPR"], [/^Asia\/Dubai$/, "AED"], [/^Asia\/Singapore$/, "SGD"], [/^Asia\/Tokyo$/, "JPY"],
  [/^Europe\/(London|Belfast|Guernsey|Isle_of_Man|Jersey)$/, "GBP"],
  [/^Europe\/(Amsterdam|Andorra|Athens|Berlin|Bratislava|Brussels|Dublin|Helsinki|Lisbon|Ljubljana|Luxembourg|Madrid|Malta|Monaco|Paris|Riga|Rome|San_Marino|Tallinn|Vaduz|Vatican|Vienna|Vilnius|Zagreb)$/, "EUR"],
  [/^Atlantic\/(Canary|Madeira|Azores)$/, "EUR"],
  [/^Australia\//, "AUD"],
  [/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|St_Johns|Regina|Montreal|Moncton|Whitehorse|Yellowknife)$/, "CAD"],
];
const REGION_CURRENCY = {
  US: "USD", IN: "INR", TH: "THB", ID: "IDR", NP: "NPR", AE: "AED", SG: "SGD", JP: "JPY", GB: "GBP", AU: "AUD", CA: "CAD",
  ...Object.fromEntries("AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK".split(" ").map(c => [c, "EUR"])),
};

export function detectCurrency({ timeZone, languages } = {}) {
  try {
    const tz = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    for (const [re, code] of TZ_CURRENCY) if (re.test(tz)) return code;
    const langs = languages ?? (typeof navigator !== "undefined" ? (navigator.languages || [navigator.language]) : []);
    for (const l of langs) {
      const region = String(l || "").split(/[-_]/)[1]?.toUpperCase();
      if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
    }
  } catch {}
  return "USD";
}

let visitor = null;
const vListeners = new Set();
const getVisitor = () => {
  if (visitor) return visitor;
  const saved = safeGet(CUR_KEY);
  visitor = saved && CURRENCIES[saved] ? saved : detectCurrency();
  return visitor;
};

/** Set and remember the visitor's currency (the header picker). */
export function setVisitorCurrency(code) {
  if (!CURRENCIES[code]) return;
  visitor = code;
  safeSet(CUR_KEY, code);
  vListeners.forEach(l => l());
}

/** [currency, setCurrency] shared by every component on the page. */
export function useVisitorCurrency() {
  const v = useSyncExternalStore(
    (l) => { vListeners.add(l); return () => vListeners.delete(l); },
    getVisitor, getVisitor,
  );
  return [v, setVisitorCurrency];
}
